/**
 * sessionStorage wrappers for auth state.
 *
 * Why sessionStorage and not localStorage:
 *   - Tokens disappear when the tab closes (smaller blast radius if a
 *     stale tab gets compromised).
 *   - PKCE pre-redirect state must be tied to *this* tab — using a
 *     shared store leaks across tabs and breaks the round-trip.
 *
 * Why not memory: we need to survive the redirect round-trip, and a
 * single in-memory variable doesn't survive page navigation.
 */

const TOKEN_KEY = "z01.auth.tokens";
const PRE_REDIRECT_KEY = "z01.auth.pre_redirect";

export interface StoredTokens {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_at: number; // ms since epoch
}

export interface PreRedirectState {
  code_verifier: string;
  state: string;
  return_to: string;
}

export const tokenStorage = {
  load(): StoredTokens | null {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as StoredTokens;
    } catch {
      return null;
    }
  },
  save(t: StoredTokens): void {
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(t));
  },
  clear(): void {
    sessionStorage.removeItem(TOKEN_KEY);
  },
  isExpired(t: StoredTokens, skewMs = 30_000): boolean {
    return t.expires_at - skewMs <= Date.now();
  },
};

export const preRedirectStorage = {
  load(): PreRedirectState | null {
    const raw = sessionStorage.getItem(PRE_REDIRECT_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as PreRedirectState;
    } catch {
      return null;
    }
  },
  save(s: PreRedirectState): void {
    sessionStorage.setItem(PRE_REDIRECT_KEY, JSON.stringify(s));
  },
  clear(): void {
    sessionStorage.removeItem(PRE_REDIRECT_KEY);
  },
};
